import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ArrowLeft, QrCode, User } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props extends Omit<AppShellProps, 'children'> {
  event: {
    id: number
    name: string
    primaryColor: string
    secondaryColor: string
    font: string | null
    badgeWidth: number
    badgeHeight: number
    logoUrl: string | null
  }
  badge: {
    backgroundUrl: string | null
    hasBackground: boolean
  }
  translations: AppShellProps['translations'] & {
    badgePreview: string
    back: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function BadgePreview(props: Props) {
  const { event, badge, translations: t, ...shell } = props

  // Convert inches to px (96 dpi)
  const widthPx = event.badgeWidth * 96
  const heightPx = event.badgeHeight * 96

  return (
    <AppShell {...shell} translations={t}>
      <Head title={`${t.badgePreview} — ${event.name}`} />

      <div className="mx-auto max-w-3xl py-10 px-4">
        <Button
          variant="ghost"
          size="sm"
          className="mb-6 text-[#91918E]"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t.back}
        </Button>

        <h1 className="text-xl font-semibold text-[#37352F] mb-6">{t.badgePreview}</h1>

        <div className="flex justify-center">
          <Card className="overflow-hidden" style={{ width: widthPx }}>
            <CardContent className="p-0">
              <div
                className="relative flex flex-col items-center justify-between"
                style={{
                  width: widthPx,
                  height: heightPx,
                  backgroundImage: badge.hasBackground ? `url(${badge.backgroundUrl})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundColor: badge.hasBackground ? undefined : '#fff',
                }}
              >
                {/* Logo */}
                {event.logoUrl && (
                  <div className="mt-4">
                    <img
                      src={event.logoUrl}
                      alt={event.name}
                      className="h-12 object-contain"
                    />
                  </div>
                )}

                {/* Event name */}
                <div className="px-4 text-center" style={{ fontFamily: event.font || 'sans-serif' }}>
                  <p
                    className="text-lg font-bold"
                    style={{ color: badge.hasBackground ? '#fff' : event.primaryColor }}
                  >
                    {event.name}
                  </p>
                </div>

                {/* Placeholder user info */}
                <div className="flex flex-col items-center gap-2 px-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-200">
                    <User className="h-8 w-8 text-gray-400" />
                  </div>
                  <p
                    className="text-base font-semibold"
                    style={{ color: badge.hasBackground ? '#fff' : event.primaryColor }}
                  >
                    John Doe
                  </p>
                  <p
                    className="text-xs"
                    style={{ color: badge.hasBackground ? 'rgba(255,255,255,0.8)' : event.secondaryColor }}
                  >
                    Organization
                  </p>
                </div>

                {/* QR placeholder */}
                <div className="mb-4 flex flex-col items-center gap-1">
                  <div className="flex h-20 w-20 items-center justify-center rounded bg-white shadow-sm">
                    <QrCode className="h-14 w-14 text-gray-800" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <p className="mt-4 text-center text-xs text-[#91918E]">
          {event.badgeWidth}" × {event.badgeHeight}" — preview with placeholder data
        </p>
      </div>
    </AppShell>
  )
}
